import React, { useState } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { GET_POLICIES, EVALUATE_IMAGE, GET_IMAGES } from '../graphql/queries';
import { CREATE_POLICY, UPDATE_POLICY, DELETE_POLICY } from '../graphql/mutations';
import { DataTable } from '../components/DataTable';
import { toast } from 'sonner';

const POLICY_TEMPLATES = [
  {
    name: 'No Critical CVEs',
    description: 'Block images with any critical vulnerabilities',
    rules: JSON.stringify([{ field: 'criticalCveCount', operator: 'lte', value: 0 }]),
    severity: 'error',
  },
  {
    name: 'Max Age 90 Days',
    description: 'Warn when images are older than 90 days',
    rules: JSON.stringify([{ field: 'imageAgeDays', operator: 'lte', value: 90 }]),
    severity: 'warning',
  },
  {
    name: 'Require SBOM',
    description: 'Require software bill of materials for all images',
    rules: JSON.stringify([{ field: 'hasSbom', operator: 'eq', value: true }]),
    severity: 'warning',
  },
  {
    name: 'Require Signing',
    description: 'Require cryptographic signing for production images',
    rules: JSON.stringify([{ field: 'isSigned', operator: 'eq', value: true }]),
    severity: 'error',
  },
];

function CreatePolicyDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rules, setRules] = useState('[]');
  const [severity, setSeverity] = useState('warning');
  const [createPolicy, { loading }] = useMutation(CREATE_POLICY);

  const handleTemplate = (template) => {
    setName(template.name);
    setDescription(template.description);
    setRules(template.rules);
    setSeverity(template.severity);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      JSON.parse(rules);
    } catch {
      toast.error('Invalid JSON rules');
      return;
    }
    try {
      await createPolicy({ variables: { input: { name, description, rules, severity, enabled: true } } });
      toast.success(`Policy "${name}" created`);
      onCreated();
      onClose();
      setName(''); setDescription(''); setRules('[]'); setSeverity('warning');
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="card modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, margin: '60px auto' }}>
        <h3>Create Policy</h3>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: '#636e72', marginBottom: 8, display: 'block' }}>Quick Templates:</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {POLICY_TEMPLATES.map((t) => (
              <button key={t.name} className="btn" onClick={() => handleTemplate(t)}
                style={{ fontSize: 11, padding: '4px 10px', background: '#dfe6e9', color: '#2d3436' }}>
                {t.name}
              </button>
            ))}
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} required
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #dfe6e9' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Description</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #dfe6e9' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Rules (JSON)</label>
            <textarea value={rules} onChange={e => setRules(e.target.value)} rows={5}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #dfe6e9', fontFamily: 'monospace', fontSize: 12 }} />
            <p style={{ fontSize: 11, color: '#b2bec3', marginTop: 4 }}>
              Format: [{'{"field": "criticalCveCount", "operator": "lte", "value": 0'}]
            </p>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Severity</label>
            <select value={severity} onChange={e => setSeverity(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #dfe6e9' }}>
              <option value="warning">Warning</option>
              <option value="error">Error (blocks builds)</option>
              <option value="info">Info</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn" style={{ background: '#dfe6e9', color: '#2d3436' }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !name}>{loading ? 'Creating...' : 'Create Policy'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Policies() {
  const [showCreate, setShowCreate] = useState(false);
  const [evalImageId, setEvalImageId] = useState('');
  const { data, loading, refetch } = useQuery(GET_POLICIES);
  const { data: imagesData } = useQuery(GET_IMAGES, { variables: { limit: 50 } });
  const [evalImage, { data: evalData, loading: evalLoading }] = useLazyQuery(EVALUATE_IMAGE);
  const [updatePolicy] = useMutation(UPDATE_POLICY);
  const [deletePolicy] = useMutation(DELETE_POLICY);

  const policies = data?.listPolicies || [];
  const images = imagesData?.listImages?.items || [];

  const handleToggle = async (policy) => {
    try {
      await updatePolicy({ variables: { input: { id: policy.id, enabled: !policy.enabled } } });
      toast.success(`Policy "${policy.name}" ${policy.enabled ? 'disabled' : 'enabled'}`);
      refetch();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete policy "${name}"?`)) return;
    try {
      await deletePolicy({ variables: { id } });
      toast.success(`Policy "${name}" deleted`);
      refetch();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEvaluate = () => {
    if (evalImageId) {
      evalImage({ variables: { imageId: evalImageId } });
    }
  };

  const policyColumns = [
    { accessorKey: 'name', header: 'Policy' },
    { accessorKey: 'severity', header: 'Severity', cell: ({ getValue }) => {
      const sev = getValue();
      const color = sev === 'error' ? '#d63031' : sev === 'warning' ? '#fdcb6e' : '#0984e3';
      return <span className="badge" style={{ background: color + '22', color }}>{sev}</span>;
    }},
    { accessorKey: 'enabled', header: 'Status', cell: ({ row }) => (
      <button onClick={() => handleToggle(row.original)}
        className={`badge badge-${row.original.enabled ? 'success' : 'pending'}`}
        style={{ cursor: 'pointer', border: 'none' }}>
        {row.original.enabled ? 'Enabled' : 'Disabled'}
      </button>
    )},
    { accessorKey: 'createdAt', header: 'Created', cell: ({ getValue }) => getValue() ? new Date(getValue()).toLocaleDateString() : '-' },
    { id: 'actions', header: '', cell: ({ row }) => (
      <button className="btn btn-danger" style={{ fontSize: 11, padding: '2px 8px' }}
        onClick={() => handleDelete(row.original.id, row.original.name)}>Delete</button>
    )},
  ];

  const evaluation = evalData?.evaluateImage;

  return (
    <div>
      <h2 className="page-title">Policy Engine</h2>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <p style={{ color: '#636e72', margin: 0 }}>Define rules to enforce image quality standards. Error-severity policies block builds.</p>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Create Policy</button>
        </div>
        {loading ? <div className="loading">Loading policies...</div> : (
          <DataTable columns={policyColumns} data={policies} pageSize={10} />
        )}
      </div>

      <div className="card">
        <h3>Evaluate Image</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
          <select value={evalImageId} onChange={e => setEvalImageId(e.target.value)}
            style={{ flex: 1, padding: '8px 12px', borderRadius: 6, border: '1px solid #dfe6e9' }}>
            <option value="">Select an image...</option>
            {images.map(img => <option key={img.id} value={img.id}>{img.name}</option>)}
          </select>
          <button className="btn btn-primary" onClick={handleEvaluate} disabled={!evalImageId || evalLoading}>
            {evalLoading ? 'Evaluating...' : 'Evaluate'}
          </button>
        </div>

        {evaluation && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span className={`badge badge-${evaluation.overallPass ? 'success' : 'error'}`} style={{ fontSize: 14 }}>
                {evaluation.overallPass ? 'PASS' : 'FAIL'}
              </span>
              <span style={{ fontSize: 12, color: '#636e72' }}>
                Evaluated at {new Date(evaluation.evaluatedAt).toLocaleString()}
              </span>
            </div>
            <table className="table">
              <thead><tr><th>Policy</th><th>Result</th><th>Severity</th><th>Details</th></tr></thead>
              <tbody>
                {evaluation.results.map((r, i) => (
                  <tr key={i}>
                    <td>{r.policyName}</td>
                    <td><span className={`badge badge-${r.pass ? 'success' : 'error'}`}>{r.pass ? 'Pass' : 'Fail'}</span></td>
                    <td>{r.severity}</td>
                    <td style={{ fontSize: 12, color: '#636e72' }}>
                      {r.violations?.length > 0 ? r.violations.join('; ') : r.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CreatePolicyDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={refetch} />
    </div>
  );
}
